/**
 * GA4 dataLayer engine — 85SIXTY GA4 & Data Layer Requirements (2026-07-01).
 *
 * Bootstrapped by snippets/ga4-datalayer.liquid (dataLayer init, page_view,
 * GTM loader, window.GA4DL context). Item data comes from the JSON registries
 * rendered by snippets/ga4-item-registry.liquid, merged into a variant map
 * persisted in sessionStorage.
 *
 * Fires: site_search, view_item_list, view_item, add_to_cart,
 * remove_from_cart, and page_view on load-more pagination. begin_checkout and
 * purchase fire from the checkout web pixel (theme code cannot run there).
 *
 * user_data (customer_id / customer_type / user_email / shop_status) is pushed
 * from snippets/ga4-datalayer.liquid, not here: it must land before page_view,
 * which is pushed during head render. It replaced login_success, removed in
 * 2026-09 because the external OAuth flow gives no reliable on-site signal.
 *
 * Cart tracking is response-driven: /cart.js|.json 503 intermittently on this
 * store, so mutations are diffed from /cart/add|change|update responses
 * against the server-rendered GA4DL.cartSeed baseline instead of re-fetching.
 * Cart state is keyed by VARIANT ID with quantities summed across lines: this
 * store regenerates the Shopify line-key hash on every quantity change, so
 * keying by line key double-reported every change (see cartKey).
 */
(function () {
  'use strict';

  var DL = (window.dataLayer = window.dataLayer || []);
  var CFG = window.GA4DL || {};
  var CURRENCY = CFG.currency || (window.armadaMoney && window.armadaMoney.currency) || 'USD';
  var REG_KEY = 'ga4dl:registry';
  var CK_CAT_COOKIE = '_ga4_ckcat';
  var registry = {};
  var cartState = null;

  function round2(n) {
    return Math.round((Number(n) || 0) * 100) / 100;
  }

  function textOf(el) {
    return (el && el.textContent ? el.textContent : '').replace(/\s+/g, ' ').trim();
  }

  function slugify(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function pushEcom(event, ecommerce) {
    DL.push({ ecommerce: null });
    DL.push({ event: event, ecommerce: ecommerce });
  }

  function loadStoredRegistry() {
    try {
      var s = sessionStorage.getItem(REG_KEY);
      if (s) registry = JSON.parse(s) || {};
    } catch (e) {
      registry = {};
    }
  }

  /* Persist a bounded snapshot (most recent 500 variants) WITHOUT trimming the
     in-memory registry — trimming it in place could evict this page's own
     products before view_item_list is built on large PLPs. */
  function storeRegistry() {
    try {
      var keys = Object.keys(registry);
      var persist = registry;
      if (keys.length > 500) {
        persist = {};
        keys.slice(keys.length - 500).forEach(function (k) {
          persist[k] = registry[k];
        });
      }
      sessionStorage.setItem(REG_KEY, JSON.stringify(persist));
    } catch (e) {
      /* storage unavailable */
    }
  }

  function scanRegistry(root) {
    var blobs = (root || document).querySelectorAll('script[data-ga4-items]');
    var found = false;
    blobs.forEach(function (blob) {
      try {
        var map = JSON.parse(blob.textContent);
        if (!map) return;
        if (map.variants) {
          // Compact format: product-level "shared" fields + per-variant deltas.
          var shared = map.shared || {};
          Object.keys(map.variants).forEach(function (vid) {
            registry[vid] = Object.assign({}, shared, map.variants[vid]);
            found = true;
          });
        } else {
          // Legacy flat map (also the sessionStorage persistence format).
          Object.keys(map).forEach(function (vid) {
            registry[vid] = map[vid];
            found = true;
          });
        }
      } catch (e) {
        /* malformed blob */
      }
    });
    if (found) storeRegistry();
  }

  function brandFromVendor(vendor) {
    var first = String(vendor || '').split('-')[0].trim();
    if (!first) return 'Armada';
    return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
  }

  /* Last-resort item for a cart line with no registry entry — a product whose
     card/PDP was never rendered in this session. item_category is omitted
     rather than guessed: it comes from the product template, which the Cart
     API does not expose. Every surface that renders a product also renders the
     registry, so this path is rare. */
  function itemFromCartLine(line) {
    return {
      item_id: line.sku || String(line.variant_id),
      item_name: line.product_title || line.title || '',
      item_brand: brandFromVendor(line.vendor),
      item_category2: String(line.product_type || '').toLowerCase(),
      item_variant: line.variant_title || '',
      price: round2((line.final_price != null ? line.final_price : line.price) / 100)
    };
  }

  /* Cart line data (final price, line-level coupon/discount) overrides registry values. */
  function itemFor(variantId, line, quantity) {
    var base = registry[String(variantId)];
    var item = base ? Object.assign({}, base) : line ? itemFromCartLine(line) : null;
    if (!item) return null;
    if (line) {
      var finalUnit = line.final_price != null ? line.final_price : line.price;
      item.price = round2(finalUnit / 100);
      var discUnit = (line.original_price != null ? line.original_price : finalUnit) - finalUnit;
      if (discUnit > 0) item.discount = round2(discUnit / 100);
      var codes = [];
      (line.line_level_discount_allocations || []).forEach(function (a) {
        var app = a && a.discount_application;
        if (app && app.title && codes.indexOf(app.title) === -1) codes.push(app.title);
      });
      if (codes.length) item.coupon = codes.join(',');
    }
    if (quantity != null) item.quantity = quantity;
    return item;
  }

  /* Cart state is keyed by VARIANT ID, never by the Shopify line key.
     A line key is a variantId:hash pair, and this store regenerates the hash on
     every quantity change - verified on qual against a line with empty
     properties and no selling plan, where 3 -> 4 produced a brand new key.
     Keying on it made one quantity change look like the whole old line removed
     and a whole new line added, so a 3 -> 4 change emitted remove_from_cart
     qty 3 alongside add_to_cart qty 4 instead of add_to_cart qty 1.
     Quantities are summed per variant by the callers, so a variant spread over
     several lines still nets the correct delta. */
  function cartKey(line) {
    return String(line.variant_id || line.key);
  }

  function seedCartState() {
    var map = {};
    (CFG.cartSeed || []).forEach(function (line) {
      var key = cartKey(line);
      if (map[key]) {
        map[key].qty += line.quantity;
      } else {
        map[key] = { qty: line.quantity, line: line };
      }
    });
    cartState = map;
  }

  function snapshotCart(cart) {
    var map = {};
    (cart.items || []).forEach(function (line) {
      var key = cartKey(line);
      if (map[key]) {
        map[key].qty += line.quantity;
      } else {
        map[key] = { qty: line.quantity, line: line };
      }
    });
    return map;
  }

  function pushCartEvent(event, items) {
    if (!items.length) return;
    pushEcom(event, {
      currency: CURRENCY,
      value: round2(
        items.reduce(function (sum, it) {
          return sum + (it.price || 0) * (it.quantity || 1);
        }, 0)
      ),
      items: items
    });
  }

  function diffAndPush(prev, next) {
    var adds = [];
    var removes = [];
    var keys = {};
    Object.keys(prev).forEach(function (k) {
      keys[k] = true;
    });
    Object.keys(next).forEach(function (k) {
      keys[k] = true;
    });
    Object.keys(keys).forEach(function (k) {
      var before = prev[k] ? prev[k].qty : 0;
      var after = next[k] ? next[k].qty : 0;
      var delta = after - before;
      if (!delta) return;
      var src = (delta > 0 ? next[k] : next[k] || prev[k]).line;
      var item = itemFor(src.variant_id, src, Math.abs(delta));
      if (!item) return;
      (delta > 0 ? adds : removes).push(item);
    });
    pushCartEvent('add_to_cart', adds);
    pushCartEvent('remove_from_cart', removes);
  }

  /* /cart/add response lines carry the RESULTING line quantity. */
  function handleAddResponse(json) {
    if (!json) return;
    var lines = Array.isArray(json.items) ? json.items : json.variant_id ? [json] : [];
    if (!lines.length) return;
    if (!cartState) cartState = {};
    var adds = [];
    lines.forEach(function (line) {
      var k = cartKey(line);
      var prevQty = cartState[k] ? cartState[k].qty : 0;
      var newQty = Number(line.quantity) || 0;
      var delta = newQty - prevQty;
      if (delta > 0) {
        var it = itemFor(line.variant_id, line, delta);
        if (it) adds.push(it);
      }
      cartState[k] = { qty: newQty, line: line };
    });
    pushCartEvent('add_to_cart', adds);
    writeCheckoutCategoryCookie();
  }

  /* /cart/change|update|clear responses carry the full cart. */
  function handleFullCart(json) {
    if (!json || !Array.isArray(json.items)) return;
    var next = snapshotCart(json);
    if (cartState) diffAndPush(cartState, next);
    cartState = next;
    writeCheckoutCategoryCookie();
  }

  /* Checkout-pixel line data has no gender/sale status/series/PIM season, so the
     cart variants' registry item_brand (b), item_category3 (c3), item_category4
     (c4) and item_category5 (c5) are bridged to the pixel via the _ga4_ckcat cookie. */
  /* Bridge product fields the checkout pixel cannot resolve itself. A web pixel
     receives only product.type — never the Shopify taxonomy, a metafield, or a
     template — so item_brand and item_category1..5 are handed over in a cookie
     keyed by variant id.

     Format is a string dictionary plus index rows: {d:[...unique strings...],
     v:{variantId:[b,c1,c2,c3,c4,c5]}} with -1 for a missing field. Carts repeat
     the same categories heavily, so interning them raised capacity from 17 to
     73 distinct variants inside the ~3500 byte budget once item_category and
     item_category2 became free text (a taxonomy leaf and a PIM code) rather
     than a five-value enum. The pixel accepts this and the older flat format. */
  function writeCheckoutCategoryCookie() {
    try {
      /* _ga4_ckcat exists purely to feed analytics (the checkout pixel), so it
         is an analytics cookie and must not exist before analytics consent.
         Denied or undecided: actively expire it — a visitor who revokes
         consent must not keep carrying pre-consent data. bindConsentRefresh()
         re-runs this on every consent change, so a later grant writes it and
         a later revocation clears it. */
      if (!analyticsConsentGranted()) {
        document.cookie = CK_CAT_COOKIE + '=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
        return;
      }
      if (!cartState) return;
      var FIELDS = ['item_brand', 'item_category', 'item_category2', 'item_category3', 'item_category4', 'item_category5'];
      var vids = [];
      Object.keys(cartState).forEach(function (k) {
        var vid = String(cartState[k].line.variant_id);
        if (registry[vid] && vids.indexOf(vid) === -1) vids.push(vid);
      });

      /* Encode the first `count` variants; the dictionary is rebuilt each time
         so a trimmed payload never carries strings nothing references. */
      function encode(count) {
        var dict = [];
        var index = {};
        var rows = {};
        vids.slice(0, count).forEach(function (vid) {
          var it = registry[vid];
          rows[vid] = FIELDS.map(function (f) {
            var val = it[f];
            if (!val) return -1;
            if (!(val in index)) {
              index[val] = dict.length;
              dict.push(val);
            }
            return index[val];
          });
        });
        return encodeURIComponent(JSON.stringify({ d: dict, v: rows }));
      }

      var n = vids.length;
      var val = encode(n);
      while (val.length > 3500 && n > 0) {
        n -= 1;
        val = encode(n);
      }
      document.cookie = CK_CAT_COOKIE + '=' + val + '; path=/; max-age=86400; SameSite=Lax';
    } catch (e) {
      /* cookie unavailable */
    }
  }

  function bindCartEvents() {
    var originalFetch = window.fetch;
    window.fetch = function () {
      var arg = arguments[0];
      var url = typeof arg === 'string' ? arg : arg && arg.url ? arg.url : '';
      var promise = originalFetch.apply(this, arguments);
      var isAdd = /\/cart\/add/.test(url);
      var isChange = /\/cart\/(change|update|clear)/.test(url);
      if (isAdd || isChange) {
        promise
          .then(function (res) {
            if (!res || !res.ok) return;
            res
              .clone()
              .json()
              .then(function (json) {
                if (json && json.status && json.message) return; // Shopify cart error payload
                scanRegistry();
                if (isAdd) {
                  handleAddResponse(json);
                } else {
                  handleFullCart(json);
                }
              })
              .catch(function () {});
          })
          .catch(function () {});
      }
      return promise;
    };
  }

  function listContext() {
    if (CFG.pageType === 'search') {
      return { id: 'search_results', name: 'Search Results' };
    }
    // Breadcrumb trail, skipping the locale-dependent "Home" crumb.
    var crumbs = [];
    document.querySelectorAll('nav[data-breadcrumb-context] li[data-crumb]').forEach(function (li) {
      if (li.getAttribute('data-crumb') === 'home') return;
      var t = textOf(li.querySelector('a, span') || li);
      if (t) crumbs.push(t);
    });
    if (!crumbs.length && CFG.collection && CFG.collection.title) {
      crumbs = [CFG.collection.title];
    }
    if (!crumbs.length) {
      var title = (document.title || '').split('–')[0].trim();
      crumbs = title ? [title] : [];
    }
    return {
      id: crumbs.map(slugify).join('_'),
      name: crumbs.join(' > ')
    };
  }

  /* blobList: only the cards appended by the last load-more page. Omitted on the
     initial render, where the whole grid is reported. */
  function pushListView(blobList) {
    if (CFG.pageType !== 'collection' && CFG.pageType !== 'search') return;
    var grid = document.getElementById('product-grid');
    if (!grid) return;
    var blobs = blobList || grid.querySelectorAll('script[data-ga4-items][data-ga4-context="card"]');
    var items = [];
    blobs.forEach(function (blob) {
      if (items.length >= 100) return;
      var vid = blob.getAttribute('data-ga4-default-variant');
      var item = itemFor(vid, null, null);
      if (item) {
        delete item.quantity;
        items.push(item);
      }
    });
    if (!items.length) return;
    var ctx = listContext();
    pushEcom('view_item_list', {
      currency: CURRENCY,
      item_list_id: ctx.id,
      item_list_name: ctx.name,
      items: items
    });
  }

  function pushViewItem() {
    if (CFG.pageType !== 'product' || !CFG.product) return;
    var item = itemFor(CFG.product.selectedVariantId, null, 1);
    if (!item) return;
    pushEcom('view_item', {
      currency: CURRENCY,
      value: round2((item.price || 0) * 1),
      items: [item]
    });
  }

  function pushSearch(query, type, resultItem) {
    var payload = {
      event: 'site_search',
      search_query: String(query || '').trim(),
      search_type: type,
      page_title: document.title,
      page_location: window.location.href
    };
    if (resultItem) payload.search_result_item = resultItem;
    DL.push(payload);
  }

  /* Classify a submitted form as a real search, or not a search at all.
     "Contains an input named q" is not enough: the facet and filter panel forms
     carry a hidden q input purely to preserve the search term across filtering,
     so every filter or sort change on a search results page fired a phantom
     site_search with the same query (verified on qual). Both genuine search
     forms post to the search endpoint; the filter forms carry no action. */
  function searchFormType(form) {
    if (!form || !form.querySelector || !form.matches) return null;
    if (!form.querySelector('input[name="q"]')) return null;
    var FILTERS = '.facets__form, .armada-filter-panel__form';
    if (form.matches(FILTERS) || form.closest(FILTERS)) return null;
    if ((form.getAttribute('action') || '').indexOf('/search') === -1) return null;
    return form.closest('search-drawer') ? 'modal search' : 'results page search';
  }

  function bindSearch() {
    // Icon click / Enter in the search drawer form or the results-page form.
    document.addEventListener(
      'submit',
      function (e) {
        var type = searchFormType(e.target);
        if (!type) return;
        pushSearch(e.target.querySelector('input[name="q"]').value, type);
      },
      true
    );

    // Clicks on modal suggestions / product results / pages.
    document.addEventListener(
      'click',
      function (e) {
        if (!e.target || !e.target.closest) return;
        var drawer = e.target.closest('search-drawer');
        if (!drawer) return;
        var link = e.target.closest('a');
        if (!link) return;
        var input = drawer.querySelector('input[name="q"]');
        var query = input ? input.value : '';
        if (link.classList.contains('search-drawer__suggestion')) {
          var isPage = !!link.closest('.predictive-search__pages-wrapper');
          pushSearch(query, isPage ? 'search modal pages' : 'search modal suggestions', textOf(link));
        } else if (
          link.classList.contains('search-drawer__item') ||
          link.classList.contains('predictive-search__item')
        ) {
          var titleEl = link.querySelector('.search-drawer__item-title');
          pushSearch(query, 'search modal results', titleEl ? textOf(titleEl) : textOf(link));
        }
      },
      true
    );
  }

  function analyticsConsentGranted() {
    try {
      var api = window.Shopify && window.Shopify.customerPrivacy;
      return !!(api && typeof api.analyticsProcessingAllowed === 'function' && api.analyticsProcessingAllowed());
    } catch (e) {
      return false;
    }
  }

  function cardBlobs() {
    var grid = document.getElementById('product-grid');
    return grid ? grid.querySelectorAll('script[data-ga4-items][data-ga4-context="card"]') : [];
  }

  /* Load-more does not change document.location, so page_location is rebuilt with
     the ?page=N the pager just fetched - otherwise page 2 is indistinguishable
     from page 1 in GA4. page_path stays query-free per spec 1.1. */
  function paginatedHref(page) {
    try {
      var u = new URL(window.location.href);
      u.searchParams.set('page', String(page));
      return u.href;
    } catch (e) {
      return window.location.href;
    }
  }

  function bindPagination() {
    // Spec 4.1: load-more pagination counts as a page_view (infinite scroll does not).
    var pageNo = (function () {
      var n = 1;
      try {
        n = parseInt(new URLSearchParams(window.location.search).get('page'), 10);
      } catch (e) {}
      return n > 0 ? n : 1;
    })();

    document.addEventListener(
      'click',
      function (e) {
        if (!e.target || !e.target.closest) return;
        if (!e.target.closest('.btn--load-more')) return;
        if (!document.getElementById('product-grid')) return;

        /* Confirm the grid actually grew before reporting a page view - the old
           fixed timer fired even when the fetch failed or returned no results. */
        var before = cardBlobs().length;
        var fromHref = window.location.href;
        var waited = 0;
        var poll = setInterval(function () {
          waited += 200;
          var blobs = cardBlobs();
          if (blobs.length > before) {
            clearInterval(poll);
            pageNo += 1;
            scanRegistry();
            DL.push({
              event: 'page_view',
              page_category: CFG.pageCategory,
              page_location: paginatedHref(pageNo),
              page_path: window.location.pathname,
              page_referrer: fromHref,
              page_title: document.title
            });
            pushListView(Array.prototype.slice.call(blobs, before));
          } else if (waited >= 8000) {
            clearInterval(poll); // load failed or exhausted - no page_view
          }
        }, 200);
      },
      true
    );

    // Re-scan the registry when the grid re-renders so quick add keeps resolving.
    var grid = document.getElementById('product-grid');
    var container = grid ? grid.closest('#ProductGridContainer') || grid.parentElement : null;
    if (container && 'MutationObserver' in window) {
      var scheduled = false;
      new MutationObserver(function () {
        if (scheduled) return;
        scheduled = true;
        setTimeout(function () {
          scheduled = false;
          scanRegistry();
        }, 250);
      }).observe(container, { childList: true, subtree: true });
    }
  }

  /* The Customer Privacy API loads via content_for_header, AFTER this script's
     init — so the cookie write at init usually sees "consent unknown" and
     expires instead. Re-run it once the API answers (poll) and on every later
     consent change (listener), covering both the returning granted visitor and
     a banner answered minutes into the session. */
  function bindConsentRefresh() {
    document.addEventListener('visitorConsentCollected', writeCheckoutCategoryCookie);
    var tries = 0;
    var timer = setInterval(function () {
      var api = window.Shopify && window.Shopify.customerPrivacy;
      if (api || ++tries >= 40) {
        clearInterval(timer);
        writeCheckoutCategoryCookie();
      }
    }, 250);
  }

  function init() {
    loadStoredRegistry();
    scanRegistry();
    seedCartState();
    writeCheckoutCategoryCookie();
    bindConsentRefresh();
    pushListView();
    pushViewItem();
    bindSearch();
    bindCartEvents();
    bindPagination();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
